import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, UserPlus, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { investorRolesAPI, usersAPI } from '../../../services/api';
import { custodianRolesAPI } from '../../../services/custodianRolesAPI';
import { facilityAgentRolesAPI } from '../../../services/facilityAgentRolesAPI';
import { issuerRolesAPI } from '../../../services/issuerRolesAPI';
import { superAdminRolesAPI } from '../../../services/superAdminRolesAPI';
import { primarySubscriberRolesAPI } from '../../../services/primarySubscriberRolesAPI';
import { getRoleModelForUser } from '../../../utils/roleModelMapping';

const CreateSubAccountModal = ({ isOpen, onClose, onCreate, isCreating, currentUserRole }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    assignedRoleId: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  // Fetch available roles for investor or custodian
  const { data: rolesData, isLoading: rolesLoading, error: rolesError } = useQuery({
    queryKey: ['roles', currentUserRole],
    queryFn: async () => {
      if (currentUserRole === 'investor') {
        const response = await investorRolesAPI.getRoles();
        return response;
      } else if (currentUserRole === 'custodian') {
        const response = await custodianRolesAPI.getRoles();
        return response;
      } else if (currentUserRole === 'facility_agent') {
        const response = await facilityAgentRolesAPI.getRoles();
        return response;
      } else if (currentUserRole === 'issuer') {
        const response = await issuerRolesAPI.getRoles();
        return response;
      } else if (currentUserRole === 'super_admin') {
        const response = await superAdminRolesAPI.getRoles();
        return response;
      } else if (currentUserRole === 'primary_subscriber') {
        const response = await primarySubscriberRolesAPI.getRoles();
        return response;
      }
      return Promise.resolve({ data: { data: { roles: [] } } });
    },
    select: (data) => {
      // Handle different possible response structures
      let roles = [];

      // Check for axios response structure first
      if (data?.data?.data?.roles) {
        // Axios response: { data: { success, message, data: { roles: [...] } } }
        roles = data.data.data.roles;
      } else if (data?.data?.roles) {
        // Standard response: { success, message, data: { roles: [...] } }
        roles = data.data.roles;
      } else if (data?.roles) {
        // Direct response: { roles: [...] }
        roles = data.roles;
      } else if (Array.isArray(data)) {
        // Array response: [...]
        roles = data;
      }

      return roles;
    },
    enabled: (currentUserRole === 'investor' || currentUserRole === 'custodian' || currentUserRole === 'facility_agent' || currentUserRole === 'issuer' || currentUserRole === 'super_admin' || currentUserRole === 'primary_subscriber') && isOpen
  });



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Phone number validation
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }

    // Role validation
    if ((currentUserRole === 'investor' || currentUserRole === 'custodian' || currentUserRole === 'facility_agent' || currentUserRole === 'issuer' || currentUserRole === 'super_admin' || currentUserRole === 'primary_subscriber') && !formData.assignedRoleId) {
      newErrors.assignedRoleId = 'Please select a role for the sub-account';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Check email availability before submitting
    setIsCheckingEmail(true);
    try {
      const response = await usersAPI.checkEmailAvailability(formData.email);
      const isAvailable = response?.data?.data?.isAvailable;
      
      if (!isAvailable) {
        toast.error('This email is already in use. Please use a different email address');
        setErrors(prev => ({ ...prev, email: 'Email is already in use' }));
        setIsCheckingEmail(false);
        return;
      }
    } catch (error) {
      toast.error('Failed to verify email availability. Please try again');
      setIsCheckingEmail(false);
      return;
    }
    setIsCheckingEmail(false);

    // Prepare the data for sub-account creation
    // Backend will decide whether to create directly or submit for approval
    const subAccountData = {
      email: formData.email,
      password: formData.password,
      role: currentUserRole || 'super_admin', // Sub-accounts will have the same role as parent
      personalInfo: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber
      },
      isSubAccount: true, // Flag to identify this as a sub-account
      accountType: 'corporate', // Sub-accounts are always corporate type
      isEmailVerified: true, // Skip email verification for sub-accounts
      subAccountPermissions: [], // Default empty permissions
      assignedRoleId: formData.assignedRoleId || undefined,
      assignedRoleModel: formData.assignedRoleId ? getRoleModelForUser(currentUserRole) : undefined
    };

    onCreate(subAccountData);
  };

  const handleClose = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      assignedRoleId: ''
    });
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b borderlight-gray">
          <div className="flex items-center">
            <UserPlus className="w-6 h-6 mr-3 text-primary-600" />
            <h3 className="text-lg font-medium text-dark-gray">
              Create {currentUserRole === 'issuer' ? 'Issuer' :
                currentUserRole === 'custodian' ? 'Custodian' :
                  currentUserRole === 'facility_agent' ? 'Facility Agent' :
                    currentUserRole === 'investor' ? 'Investor' :
                      currentUserRole === 'primary_subscriber' ? 'Primary Subscriber' :
                        'Super Admin'} Sub Account
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="transition-colors text-dark-gray hover:text-light-gray"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="email" className="block mb-1 text-sm font-medium text-dark-gray">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`input placeholder-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${errors.email ? 'border-error-500' : 'border-light-gray'}`}
              placeholder="Enter email address"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-error-600">{errors.email}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block mb-1 text-sm font-medium text-dark-gray">
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`input placeholder-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${errors.firstName ? 'border-error-500' : 'border-light-gray'}`}
                placeholder="Enter first name"
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-error-600">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label htmlFor="lastName" className="block mb-1 text-sm font-medium text-dark-gray">
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`input placeholder-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${errors.lastName ? 'border-error-500' : 'border-light-gray'}`}
                placeholder="Enter last name"
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-error-600">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="phoneNumber" className="block mb-1 text-sm font-medium text-dark-gray">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              className={`input placeholder-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${errors.phoneNumber ? 'border-error-500' : 'border-light-gray'}`}
              placeholder="Enter phone number"
            />
            {errors.phoneNumber && (
              <p className="mt-1 text-sm text-error-600">{errors.phoneNumber}</p>
            )}
          </div>

          {/* Role Assignment */}
          {(currentUserRole === 'investor' || currentUserRole === 'custodian' || currentUserRole === 'facility_agent' || currentUserRole === 'issuer' || currentUserRole === 'super_admin' || currentUserRole === 'primary_subscriber') && (
            <div>
              <label htmlFor="assignedRoleId" className="block mb-1 text-sm font-medium text-dark-gray">
                Assign Role *
              </label>
              <select
                id="assignedRoleId"
                name="assignedRoleId"
                value={formData.assignedRoleId}
                onChange={handleInputChange}
                className={`input focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${errors.assignedRoleId ? 'border-error-500' : 'border-light-gray'}`}
              >
                <option value="">Select a role</option>
                {rolesLoading && <option value="" disabled>Loading roles...</option>}
                {rolesError && <option value="" disabled>Error loading roles</option>}
                {rolesData && rolesData.length > 0 ? (
                  rolesData.map((role) => (
                    <option key={role._id} value={role._id}>
                      {role.name} ({role.permissions.length} permissions)
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No roles available</option>
                )}
              </select>
              {errors.assignedRoleId && (
                <p className="mt-1 text-sm text-error-600">{errors.assignedRoleId}</p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="password" className="block mb-1 text-sm font-medium text-dark-gray">
              Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`input placeholder-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 pr-10 ${errors.password ? 'border-error-500' : 'border-light-gray'}`}
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-error-600">{errors.password}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block mb-1 text-sm font-medium text-dark-gray">
              Confirm Password *
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`input placeholder-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 pr-10 ${errors.confirmPassword ? 'border-error-500' : 'border-light-gray'}`}
                placeholder="Confirm password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-error-600">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Info Box */}
          <div className="p-4 border rounded-md bg-info-50 border-info-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <UserPlus className="w-5 h-5 text-info-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-info-800">
                  Sub Account Information
                </h3>
                <div className="mt-2 text-sm text-info-700">
                  <p>• This sub-account will inherit the same corporate information as your account</p>
                  <p>• You can assign a specific role to control their access permissions</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-4 space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={isCreating || isCheckingEmail}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || isCheckingEmail}
              className="btn-primary"
            >
              {isCheckingEmail ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></div>
                  Verifying...
                </div>
              ) : isCreating ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></div>
                  Creating...
                </div>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSubAccountModal; 